import { Action } from "redux";

import { IDataSourceConditions, IDataSourceInitConfig } from "@multiweb/reactCore/datasource";
import { IPublicAttributeInfo } from "@multiweb/types/contracts";
import { IconCode } from "@multiweb/types/contracts/iconCode";
import { IOrderBy } from "@multiweb/types/multiweb";
import { EncryptedRecordType, RecordType, TypedObject } from "@multiweb/types/react";
import {
    DELETE_FRAME,
    HANDLE_RECORD_CLICK,
    INIT_FRAME,
    INIT_FRAME_WITH_DATASOURCE,
    SET_ACTION_AREA,
    SET_ALLOW_MULTISELECT,
    SET_CAN_VALUE_LOAD,
    SET_EMPTY_MESSAGE_CUSTOMIZATION,
    SET_INTERACTIVE,
    SET_LOAD_ON_SCROLL,
    SET_MULTISELECT_MODE,
    SET_MULTIWEB_VISIBILITY,
    SET_ROW_TEMPLATE,
    SET_SELECTED_RECORDS,
    SET_SHOW_HEADER,
    TRY_SELECT_RECORD,
} from "./constants";

/**
 * Výčtový typ, který určuje umístění akcí v DataRow.
 */
export enum ActionArea {
    aaRight = "aaRight",
    aaLeft = "aaLeft",
    aaBottom = "aaBottom",
}
/**
 * Typ určuje šablonu řádku. Lze použít buď standartní šablonu z výčtu existujících nebo zadat vlastní.
 */
export type RowTemplateType = DefaultRowTemplate | string;
/**
 * Výčtový typ. který určuje template pro zobrazení dat v DataRow.
 */
export enum DefaultRowTemplate {
    singleLine = "SingleLine",
    multiLine1 = "MultiLine1",
    multiLine2 = "MultiLine2",
    multiLine3 = "MultiLine3",
}

/**
 * Výčtový typ atributů pro Výběr hráčů
 */
export enum ParticipantsAttributes {
    participantType = "participanttype",
    personName = "personname",
    actualRoleName = "actualrolename",
    name = "name",
    roleUsers = "roleusers",
    id = "id",
}

/**
 * Customizace ikony nebo nebo textu, pokud je frame prázdný.
 */
export type EmptyMessageSettings = {
    /** Lokalizace, která se zobrazí, pokud je seznam prázdný.  */
    readonly noItemsLocKey: string;
    /** Kód ikony, která se zobrazí, pokud je seznam prázdný.  */
    readonly noItemsIcon: IconCode;
    /** Lokalizace, která se zobrazí, pokud je aktivní filtr a není nalezen žádný záznam.  */
    readonly noItemsFilterLocKey: string;
    /** Kód ikony, která se zobrazí, pokud je aktivní filtr a není nalezen žádný záznam  */
    readonly noItemsFilterIcon: IconCode;
    /** Lokalizace, která se zobrazí, pokud je aktivní fulltextové vyhledávání a není nalezen žádný záznam. Jako první parametr (@0) dostane hledaný text. */
    readonly noItemsFulltextSearchLocKey: string;
    /** Kód ikony, která se zobrazí, pokud je aktivní fulltextové vyhledávání a není nalezen žádný záznam  */
    readonly noItemsFulltextSearchIcon: IconCode;
};

/**
 * Customizace ikony nebo nebo textu, pokud je frame prázdný. (jen výčet změn)
 */
export type EmptyMessageCustomization = Partial<EmptyMessageSettings>;

/**
 * Definice stavu pro mapu framů.
 */
export type FramesMapState = TypedObject<IFramesState>;

export interface IFramesState {
    /** Určuje, zda je možné načítat hodnotu. */
    readonly canValueLoad: boolean;
    /**
     * Určuje šablonu, která bude použita pro vykreslení řádků.
     * Hodnota je shodná jako klíče rendereru.
     */
    readonly rowTemplate: RowTemplateType;
    /** Určuje do které oblasti se zobrazují akce záznamu. */
    readonly actionArea: ActionArea;
    /** Určuje, zda se má zobrazit hlavička. */
    readonly showHeader: boolean;
    /** Určuje, zda se mají data načítat postupně při scrollovaní nebo se zobrazit pouze do viditelné části. */
    readonly loadOnScroll: boolean;
    /** Určuje, zda je frame interaktivní, zda s ním lze pracovat a pouštět nad ním akce. */
    readonly interactive: boolean;
    /** Určuje, zda je povolen výběr více záznamů. */
    readonly allowMultiselect: boolean;
    /** Určuje, zda je frame viditelný. */
    readonly visibility: boolean;
    /** Customizace ikony nebo nebo textu, pokud je frame prázdný. */
    readonly emptyMessageCustomization: EmptyMessageSettings;
}

/** Konfigurační objekt použitý v akci pro inicializaci framu. */
export type InitFrameConfig = {
    /** Počáteční oblast, do které se mají akce vykreslit. */
    readonly initialActionArea?: ActionArea;
    /** Název šablony (zároveň i klíč pro renderer) */
    readonly rowTemplate?: string;
    /** Určuje, zda se má zobrazit hlavička. */
    readonly initialShowHeader?: boolean;
    /** Určuje, zda se mají data načítat postupně při scrollovaní nebo se zobrazit pouze do viditelné části. */
    readonly initialLoadOnScroll?: boolean;
    /** Customizace ikony nebo nebo textu, pokud je frame prázdný. */
    readonly emptyMessageCustomization?: EmptyMessageCustomization;
    /** Určuje, zda je povolen výběr více záznamů. */
    readonly allowMultiselect?: boolean;
};

/**
 * Props sdílené napříč některými komponentami framů.
 */
export type FrameSharedProps = Readonly<{
    /** Unikátní klíč pro DataSource. */
    dataSourceKey: string;
    /** Unikátní klíč pro záznam. */
    recordId: string;
}>;

export type FrameAttributeSharedProps = FrameSharedProps & Readonly<{
    /** Jméno atributu. */
    attributeName: string;
}>;

/**
 * Rozhoduje, jestli se smí vyrenderovat komponenta pro zašifrovaný stav.
 * @param RecordType Záznam z DataSource.
 * @param IPublicAttributeInfo Info o atributu.
 */
export type CanRenderEncryptedDecider = (record: EncryptedRecordType, info: IPublicAttributeInfo) => boolean;

/**
 * Rozhoduje o tom, zda se může vyrenderovat komponenta pro zobrazení prázdného atributu.
 * @param record Záznam z DataSource.
 * @param info Info o atributu.
 */
export type CanRenderEmptyDecider = (record: RecordType, info: IPublicAttributeInfo) => boolean;

/**
 * Pomocná akce, kterou musí rozšiřovat akce, které jsou mapovány
 * na jednotlivé instance `Frame`.
 */
export interface IFrameMappedAction<A = string> extends Action<A> {
    readonly meta: {
        readonly frameKey: string;
    };
}

export class IFrameMappedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance?.meta?.frameKey;
    }
}

/**
 * Akce pro smazání framu.
 */
export interface IDeleteFrameAction extends IFrameMappedAction<typeof DELETE_FRAME> {}

export class IDeleteFrameAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === DELETE_FRAME;
    }
}

/** Payload akce pro inicializaci framu. */
interface IInitFrameActionPayload {
    readonly initialCanValueLoad: boolean;
    readonly initialInteractive: boolean;
    readonly frameConfig: InitFrameConfig;
}

/**
 * Akce pro inicializaci framu.
 */
export interface IInitFrameAction extends IFrameMappedAction<typeof INIT_FRAME> {
    readonly payload: IInitFrameActionPayload;
}

export class IInitFrameAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === INIT_FRAME;
    }
}

/**
 * Akce pro inicializaci framu, který využívá dataSource.
 */
export interface IInitFrameWithDataSourceAction extends IFrameMappedAction<typeof INIT_FRAME_WITH_DATASOURCE> {
    readonly payload: IInitFrameActionPayload & {
        readonly dataSourceConfig: IDataSourceInitConfig;
        readonly hasDetailForm: boolean;
        readonly initialOrderBy: ReadonlyArray<IOrderBy>;
        readonly initialConditions: Partial<IDataSourceConditions>;
    };
}

export class IInitFrameWithDataSourceAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === INIT_FRAME_WITH_DATASOURCE;
    }
}

/**
 * Akce pro zapnutí/vypnutí režimu výběru více záznamů.
 */
export interface ISetMultiselectModeAction extends IFrameMappedAction<typeof SET_MULTISELECT_MODE> {
    readonly payload: {
        readonly multiselectMode: boolean;
    };
}

export class ISetMultiselectModeAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === SET_MULTISELECT_MODE;
    }
}

/**
 * Akce pro výběr záznamů daných identifikátory.
 */
export interface ISetSelectedRecordsAction extends IFrameMappedAction {
    readonly payload: {
        readonly selectedRecordsIds: ReadonlyArray<string>;
    };
}

export class ISetSelectedRecordsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === SET_SELECTED_RECORDS;
    }
}

/**
 * Akce pro výběr záznamu daného identifikátorem.
 */
export interface ITrySelectRecordAction extends IFrameMappedAction {
    readonly payload: {
        readonly recordId: string;
    };
}

export class ITrySelectRecordAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === TRY_SELECT_RECORD;
    }
}

/**
 * Akce, která nastavuje informaci o tom, zda je možné načítat další data.
 */
export interface ISetCanValueLoadAction extends IFrameMappedAction {
    readonly payload: {
        readonly value: boolean;
    };
}

export class ISetCanValueLoadAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === SET_CAN_VALUE_LOAD;
    }
}

/**
 * Akce, která nastavuje template, podle kterého se vykreslí DataRow.
 */
export interface ISetRowTemplateAction extends IFrameMappedAction {
    readonly payload: {
        readonly rowTemplate: RowTemplateType;
    };
}

export class ISetRowTemplateAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === SET_ROW_TEMPLATE;
    }
}

/**
 * Akce, která nastavuje, do které oblasti se zobrazují akce záznamu.
 */
export interface ISetActionAreaAction extends IFrameMappedAction {
    readonly payload: {
        readonly actionArea: ActionArea;
    };
}

export class ISetActionAreaAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === SET_ACTION_AREA;
    }
}

/**
 * Akce, která nastavuje, zda se má zobrazit hlavička.
 */
export interface ISetShowHeaderAction extends IFrameMappedAction {
    readonly payload: {
        readonly showHeader: boolean;
    };
}

export class ISetShowHeaderAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === SET_SHOW_HEADER;
    }
}

/**
 * Akce, která nastavuje, zda se mají data načítat postupně při scrollovaní nebo se zobrazit pouze do viditelné části.
 */
export interface ISetLoadOnScrollAction extends IFrameMappedAction {
    readonly payload: {
        readonly loadOnScroll: boolean;
    };
}

export class ISetLoadOnScrollAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === SET_LOAD_ON_SCROLL;
    }
}

/**
 * Akce, která nastavuje informaci o tom, zda je frame interaktivní, zda s ním lze pracovat a pouštět nad ním akce.
 */
export interface ISetInteractiveAction extends IFrameMappedAction {
    readonly payload: {
        readonly value: boolean;
    };
}

export class ISetInteractiveAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === SET_INTERACTIVE;
    }
}

/**
 * Akce, která nastavuje, zda datasource může zapnout režim výběru více záznamů.
 */
export interface ISetAllowMultiselectAction extends IFrameMappedAction {
    readonly payload: {
        readonly allowMultiselect: boolean;
    };
}

export class ISetAllowMultiselectAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === SET_ALLOW_MULTISELECT;
    }
}

/**
 * Akce pro odchycení kliknutí na záznam.
 */
export interface IHandleRecordClickAction extends IFrameMappedAction {
    readonly payload: {
        readonly recordId: string;
    };
}

export class IHandleRecordClickAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === HANDLE_RECORD_CLICK;
    }
}

/**
 * Akce, která nastavuje příznak o tom, zda je frame viditelný na multiwebu.
 */
export interface ISetMultiwebVisibilityAction extends IFrameMappedAction {
    readonly payload: {
        readonly multiwebVisibility: boolean;
    };
}

export class ISetMultiwebVisibilityAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === SET_MULTIWEB_VISIBILITY;
    }
}

/**
 * Akce, která nastavuje kustomizační vlastnosti pro empty message.
 */
export interface ISetEmptyMessageCustomizationAction extends IFrameMappedAction {
    readonly payload: {
        readonly emptyMessageCustomization: EmptyMessageCustomization;
    };
}

export class ISetEmptyMessageCustomizationAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance instanceof IFrameMappedAction && instance.type === SET_EMPTY_MESSAGE_CUSTOMIZATION;
    }
}
